// Gate decisions for GenerateShipment, kept pure so they can be tested without
// mounting the component (BUGS-8257).

// Natura POA / Manaus skip the packed-vs-required check. The platform mirrors the
// same two ids in PackingCompletenessPolicyService; change both together.
export const QUALITY_CHECK_EXEMPT_STORE_IDS = [15808, 15775]

export const isQualityCheckExemptStore = storeId => QUALITY_CHECK_EXEMPT_STORE_IDS.includes(Number(storeId))

// Whether the guide being asked for RE-EMITS one the order already carries from a
// source other than this screen's per-box closes. Keyed on the label's PROVENANCE,
// not on the session boundary — it is also true for guides minted during this very
// session. The three shapes it catches:
//   1. already shipped/returned (`alreadyHasLabels`): the footer is in "invalidar
//      etiquetas anteriores" mode, about to invalidate and re-mint;
//   2. scenario B (bulk generate-labels, e.g. Natura / Estafeta / Blue Express):
//      carrier labels exist on the order and no box ever gets a shipping_label_url;
//   3. an async-generated guide while the order is still in `packing` — `alreadyHasLabels`
//      is false there because it additionally requires a shipped/returned status.
// Scenario A is the shape this must NOT catch: every /close mints a carrier label for
// that box, so a carrier label together with ANY box-level shipping_label_url means the
// labels came from this screen and the remaining cajas still owe a scan count. Reading
// that as a re-emission would waive the check for every box after the first, which is
// the bug (BUGS-8257). The platform writes `label.save!` and
// `box.update!(shipping_label_id: label.id)` in one transaction
// (PackingSessionService#generate_carrier_label_for_box), so the order-level and
// box-level views become visible together and no poll window can flip this true
// mid-session.
export const isReemission = ({ alreadyHasLabels, hasCarrierLabels, hasPerBoxLabels }) =>
  Boolean(alreadyHasLabels) || (Boolean(hasCarrierLabels) && !hasPerBoxLabels)

// The FIRST guide of an order needs the local scan count complete, for every role:
// `generate_additional_labels` authorizes a guide when the order already has one,
// it is not a quality-check waiver. A re-emission keeps the legacy behavior because
// the legacy screen has no session to restore counters from.
export const canGenerateFirstLabel = ({ validQualityCheck, isLabelReemission, storeId }) =>
  Boolean(isLabelReemission) || isQualityCheckExemptStore(storeId) || Boolean(validQualityCheck)

// THE gate for emitting a label from this screen, shared by its two entry points: the
// footer's primary button (isSaveDisabled) and the ctrl+g shortcut. Both callers read
// this one definition on purpose — while the shortcut carried its own copy it checked
// only formValid, and an 1,821-piece order shipped with nothing packed (BUGS-8257). A
// gate added here reaches both entry points; a gate added to one caller would not.
// WHY each term is a gate is documented at the footer's call site.
export const labelEmissionBlocked = ({ formValid, multiBoxAwaitingClosedBoxes, shouldBlockPreventively, packingAuditBlocked, readOnly }) =>
  !formValid || Boolean(multiBoxAwaitingClosedBoxes) || Boolean(shouldBlockPreventively) || Boolean(packingAuditBlocked) || Boolean(readOnly)

// Orders whose carrier the platform switches by itself when the boxes need more than one guide
// (Packing::MultipackageShippingMethodResolver): manual orders, created from the OMS and so with
// no channel to recalculate from, shipping a sale or a donation. Keep the order types in step
// with that resolver's ELIGIBLE_ORDER_TYPES. For these the screen leaves the multi-guide decision
// to the backend, which either moves the order to one of the brand's multi-guide methods or
// refuses with MULTIPACKAGE_NOT_SUPPORTED (forced order, nothing the brand enabled) and records it.
export const MULTI_GUIDE_SWITCH_ORDER_TYPES = ['SALE', 'DONATION']

export const backendSwitchesForMultiGuide = order =>
  order?.channel_name === 'manual' && MULTI_GUIDE_SWITCH_ORDER_TYPES.includes(order?.order_type)

export const pendingPiecesCount = productsToReviewById =>
  Object.values(productsToReviewById || {}).reduce((sum, product) => sum + Math.max(0, (product.quantity || 0) - (product.reviewedItemsCount || 0)), 0)

const isOpenBox = box => !box.is_closed && box.status !== 'closed'

// Closing the only open box while pieces are pending leaves the operator with
// nothing to scan into — the Panam dead end. The caller confirms before closing.
export const closingLastBoxWithPending = ({ boxes, boxId, pendingPieces }) => {
  if (!(pendingPieces > 0)) return false
  const anotherOpen = (boxes || []).some(box => box.id !== boxId && isOpenBox(box))
  return !anotherOpen
}

// The platform gates "+ N" per warehouse; a missing flag (older platform) reads as off.
export const bulkAddAllowed = (order, revoked) => order?.packing_bulk_add_enabled === true && !revoked

// The flag can flip while the screen is open: the bulk-scan 403 then revokes the button locally.
export const isBulkAddDisabledError = error =>
  error?.response?.status === 403 && error?.response?.data?.code === 'packing_bulk_add_disabled'
